// src/systems/placements.ts
//
// Phase 14 (CHESS-02, CHESS-06, BOARD-04): pre-pass that expands a
// Placement → BetType[] before scoring. Non-chess fiches identity-passthrough;
// chess fiches expand to all reachable board cells' bets (deduplicated,
// off-grid filtered).
//
// This is the SINGLE seam between RoundScene's placement collection and the
// scoring engine. Plan 14-03 wires scoring to consume the resulting BetType[]
// via ScoreInputs.expandedBets.
//
// "Void" note (CHESS-06): the canonical board has no void board-cells (only
// void wheel-slots). "Void origin" in CHESS-06 means "no BoardCell matching
// the placement's bet exists in boardLayout.cells" — we return [] in that
// case. The void-wheel-slot path is handled at scoring time: resolveBet on a
// void slot returns hit=false for every bet, so the payout sum is 0.

import type { Placement, BoardLayout, BetType, Fiche } from '../state/types';
import { reachable } from './chess';
import { subcellConnectedComponent } from './board';

/**
 * Expand a single placement to the list of bets it covers.
 *
 * - Non-chess fiches (no chessMove effect): returns [placement.bet].
 * - Chess fiches: walks the piece's reachable squares from the placement's
 *   origin cell, drops off-grid squares (no BoardCell at that row/col in
 *   boardLayout.grid), and returns the deduplicated list of unique BetType
 *   values (deduplicated by Bet value, not BoardCell identity).
 * - If the placement's bet is not present in boardLayout.cells (no origin
 *   resolvable), returns [] — chess fiche scores 0 for that placement
 *   (CHESS-06: void/missing origin = no expansion).
 */
/**
 * Build the set of blocker squares (keyed "row,col") from other placements.
 * Skips the placement being expanded (by ficheId) so a piece never blocks itself.
 */
function buildBlockers(
  placement: Placement,
  boardLayout: BoardLayout,
  otherPlacements: readonly Placement[],
): Set<string> {
  const blockers = new Set<string>();
  for (const other of otherPlacements) {
    if (other.ficheId === placement.ficheId) continue;
    const cell = boardLayout.cells.find((c) => betKey(c.bet) === betKey(other.bet));
    if (cell) blockers.add(`${cell.row},${cell.col}`);
  }
  return blockers;
}

export function expandPlacement(
  placement: Placement,
  boardLayout: BoardLayout,
  fiche: Fiche,
  otherPlacements?: readonly Placement[],
  extraBlockers?: ReadonlySet<string>,
): BetType[] {
  // Phase 23 (FICHE-23): tetris-shape expansion. Anchor is the cell the player
  // dropped on. Each [drow, dcol] offset addresses an absolute grid cell;
  // off-grid cells are dropped silently (RoundScene._isDragValid rejects
  // placements where this drop would happen, so this is defense-in-depth).
  if (fiche.effect?.type === 'tetrisShape') {
    const origin = boardLayout.cells.find((c) => betsEqual(c.bet, placement.bet));
    if (!origin) return [];
    const seen = new Set<string>();
    const bets: BetType[] = [];
    for (const [dr, dc] of fiche.effect.shape) {
      const cell = boardLayout.grid[origin.row + dr]?.[origin.col + dc];
      if (!cell) continue; // off-grid → drop
      const key = betKey(cell.bet);
      if (seen.has(key)) continue;
      seen.add(key);
      bets.push(cell.bet);
    }
    return bets;
  }

  // Non-chess fiches: identity passthrough.
  if (fiche.effect?.type !== 'chessMove') {
    return [placement.bet];
  }

  // Resolve the placement's origin cell.
  const origin = boardLayout.cells.find((c) => betsEqual(c.bet, placement.bet));
  if (!origin) {
    // Origin not on the board (CHESS-06: void/missing → no expansion).
    return [];
  }

  const blockers = new Set<string>([
    ...(otherPlacements ? buildBlockers(placement, boardLayout, otherPlacements) : []),
    ...(extraBlockers ?? []),
  ]);

  const reach = reachable(
    fiche.effect.piece,
    { row: origin.row, col: origin.col },
    boardLayout.dims,
    blockers,
  );

  // Always include the origin cell — the fiche was placed here and scores when it hits.
  // Reachable squares extend coverage beyond the origin.
  const seen = new Set<string>();
  const bets: BetType[] = [];
  const originKey = betKey(origin.bet);
  seen.add(originKey);
  bets.push(origin.bet);

  for (const sq of reach) {
    const cell = boardLayout.grid[sq.row]?.[sq.col];
    if (!cell) continue; // off-grid / null gap → drop
    // When a reach square is a subcell, cover its entire connected component so
    // reaching any part of a dozen/even-money group scores on the whole group.
    const targets =
      cell.bet.kind === 'dozen-sub' || cell.bet.kind === 'outside-sub'
        ? subcellConnectedComponent(boardLayout, cell)
        : [cell];
    for (const t of targets) {
      const key = betKey(t.bet);
      if (seen.has(key)) continue;
      seen.add(key);
      bets.push(t.bet);
    }
  }
  return bets;
}

/**
 * Stable string key for deduplicating BetType values in the expansion result.
 * Two bets with the same kind and same payload produce the same key.
 *
 * FI-03 fix: exhaustive never-guard in the default branch ensures TypeScript
 * emits a compile error if a new BetType variant is added without a betKey case,
 * preventing silent undefined returns that bypass Set deduplication.
 */
function betKey(b: BetType): string {
  switch (b.kind) {
    case 'straight':
      return `s:${b.number}`;
    case 'red':
    case 'black':
    case 'even':
    case 'odd':
    case 'low':
    case 'high':
      return b.kind;
    case 'dozen':
      return `d:${b.which}`;
    case 'column':
      return `c:${b.which}`;
    case 'split':
      return `sp:${b.numbers[0]}-${b.numbers[1]}`;
    case 'corner':
      return `cn:${b.numbers
        .slice()
        .sort((a, z) => a - z)
        .join('-')}`;
    case 'outside-sub':
      // Phase 21 (BOARD-21): sort numbers for canonical key — order-insensitive equality.
      return `os:${b.parentKind}:${[...b.numbers].sort((a, z) => a - z).join(',')}`;
    case 'dozen-sub':
      // Keyed by parentWhich + subIdx — the two fields that uniquely identify a subcell.
      return `ds:${b.parentWhich}:${b.subIdx}`;
    default: {
      // Exhaustiveness guard: if a new BetType variant is added without a
      // corresponding betKey case, TypeScript will flag `b` as `never` here
      // and this assignment becomes a compile error — not a silent undefined.
      const _exhaustive: never = b;
      return _exhaustive;
    }
  }
}

function betsEqual(a: BetType, b: BetType): boolean {
  return betKey(a) === betKey(b);
}
